import { SPATEM } from '_lib/api';
import { useAppDispatch } from '_store';
import { selectedRoadSegmentIdsSelector } from '_store/roads/selectors';
import { loadingSelector, spatemsSelector } from '_store/spatem/selectors';
import { spatemsSagas } from '_store/spatem/spatemsSagas';
import { useLocalization } from '@fluent/react';
import { CardContent, CircularProgress, IconButton, InputAdornment, TextField, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useRolePermissions } from '../../../hooks/useRolePermissions';
import styles from '../Device/styles';
import CardSpatemListItem from './Card';

export interface SpatemLayoutProps {
  onUpdateSpatem?: (idExpanded: string) => void;
}

const SpatemLayout: FC<SpatemLayoutProps> = ({ onUpdateSpatem }) => {
  const { l10n } = useLocalization();
  const classes = styles();
  const dispatch = useAppDispatch();
  const { canManageSpatem, blockedActionMode } = useRolePermissions();
  const writeBlockedMessage = l10n.getString('forms-rbac-readonly-action-denied');

  const spatems = useSelector(spatemsSelector);
  const loading = useSelector(loadingSelector);
  const selectedRoadSegmentIds = useSelector(selectedRoadSegmentIdsSelector);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    dispatch(
      spatemsSagas.actions.spatemsRequest({
        roadSegmentIds: selectedRoadSegmentIds,
      }),
    );
  }, [dispatch, selectedRoadSegmentIds]);

  const filterSpatem = (spatem: SPATEM) => {
    if (!filter) return true;
    const searchVal = filter.toLowerCase();
    const identifier = (spatem.identifier || '').toLowerCase();
    const name = (spatem.name || '').toLowerCase();
    const id = (spatem.id || '').toLowerCase();
    return identifier.indexOf(searchVal) > -1 || name.indexOf(searchVal) > -1 || id.indexOf(searchVal) > -1;
  };

  if (loading) {
    return (
      <CardContent
        className={classes.cardContent}
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}
      >
        <CircularProgress />
      </CardContent>
    );
  }

  const filteredSpatems = spatems?.filter(filterSpatem) || [];

  if (!spatems || spatems.length === 0) {
    return (
      <CardContent
        className={classes.cardContent}
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}
      >
        <Typography variant="body1" color="textSecondary">
          {l10n.getString('forms-spatem-empty')}
        </Typography>
      </CardContent>
    );
  }

  if (filteredSpatems.length === 0) {
    return (
      <CardContent className={classes.cardContent} style={{ overflow: 'hidden' }}>
        <div>
          <TextField
            className={classes.input}
            key={'search'}
            label={l10n.getString('forms-spatem-search')}
            InputLabelProps={{ variant: 'standard' }}
            onChange={(ev) => setFilter(ev.target.value)}
            fullWidth
            type="text"
            value={filter}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton edge="start">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: filter && (
                <IconButton aria-label="clear search" onClick={() => setFilter('')}>
                  <CloseIcon />
                </IconButton>
              ),
            }}
          />
        </div>
        <CardContent
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}
        >
          <Typography variant="body1" color="textSecondary">
            {l10n.getString('forms-spatem-empty')}
          </Typography>
        </CardContent>
      </CardContent>
    );
  }

  return (
    <CardContent className={classes.cardContent} style={{ overflow: 'hidden' }}>
      <div>
        <TextField
          className={classes.input}
          key={'search'}
          label={l10n.getString('forms-spatem-search')}
          InputLabelProps={{ variant: 'standard' }}
          onChange={(ev) => setFilter(ev.target.value)}
          fullWidth
          type="text"
          value={filter}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton edge="start">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: filter && (
              <IconButton aria-label="clear search" onClick={() => setFilter('')}>
                <CloseIcon />
              </IconButton>
            ),
          }}
        />
      </div>
      <div style={{ overflow: 'auto' }}>
        {filteredSpatems.map((item: SPATEM) => (
          <CardSpatemListItem
            asCard={true}
            key={item.id}
            spatem={item}
            text={item.name || item.identifier || item.id || ''}
            onUpdate={() => onUpdateSpatem?.(item.id)}
            writeAllowed={canManageSpatem}
            blockedActionMode={blockedActionMode}
            blockedMessage={writeBlockedMessage}
          />
        ))}
      </div>
    </CardContent>
  );
};

export default React.memo(SpatemLayout);

